import React, { useState } from "react";
import { fmtDateTime, toLocalISODate } from "../lib/dates";
import { toast } from "sonner";
import { Archive, RotateCcw, ChevronDown } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { usePagedSearch, SearchBox, Pager } from "./Pager";

const matchVoided = (v, t) => `${v.member_name || ""} ${v.receipt_no || ""} ${v.method || ""} ${v.void_reason || ""} ${v.voided_by || ""} ${v.account_name || ""}`.toLowerCase().includes(t);

export function VoidedPayments({ canRestore, onRestored }) {
  const { data, reload } = useApi("/admin/payments/voided");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const pg = usePagedSearch(data, "voided-page-size", matchVoided);
  const restore = async (v) => {
    if (!window.confirm(`Restore the $${Number(v.amount).toFixed(2)} payment for ${v.member_name}? It will count toward their balance again.`)) return;
    setBusy(v.id);
    try { await api.post(`/admin/payments/voided/${v.id}/restore`); toast.success("Payment restored to the ledger"); reload(); onRestored?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  if (!data) return null;
  return (
    <div className="mt-8" data-testid="voided-payments">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between rounded-2xl border border-gold/30 bg-parchment-card px-5 py-3 text-left transition-colors hover:bg-gold-light/40" data-testid="voided-toggle">
        <span className="flex items-center gap-2 font-serif-display text-lg font-bold text-crimson"><Archive size={18} className="text-gold" /> Deleted payments archive <span className="font-mono-accent text-sm text-muted-foreground" data-testid="voided-count">({data.length})</span></span>
        <ChevronDown size={18} className={`text-crimson transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 glass overflow-x-auto rounded-2xl">
          <div className="flex flex-wrap items-center gap-3 px-4 pt-3"><SearchBox value={pg.q} onChange={pg.setQuery} placeholder="Member name, receipt, reason, deleted by…" testId="voided-search" className="w-72" /></div>
          <Pager p={pg} total={pg.filtered.length} noun="deleted payments" testId="voided-pager" />
          <table className="w-full text-sm" data-testid="voided-table">
            <thead className="bg-crimson text-parchment"><tr>
              <th className="p-3 text-left">Deleted</th><th className="p-3 text-left">Member</th><th className="p-3 text-left">Payment</th>
              <th className="p-3 text-left">Reason</th><th className="p-3 text-left">Deleted by</th><th className="p-3 text-right">Amount</th>{canRestore && <th className="p-3" />}</tr></thead>
            <tbody>
              {pg.shown.map((v) => (
                <tr key={v.id} className="border-b border-gold/15" data-testid={`voided-row-${v.id}`}>
                  <td className="p-3 font-mono-accent text-xs">{fmtDateTime(v.voided_at)}</td>
                  <td className="p-3">{v.member_name}</td>
                  <td className="p-3 text-xs text-muted-foreground">{toLocalISODate(v.created_at)} · {v.method} · {v.account_name || "Unassigned"} · {v.receipt_no}</td>
                  <td className="p-3 text-xs italic" data-testid={`voided-reason-${v.id}`}>{v.void_reason}</td>
                  <td className="p-3 text-xs">{v.voided_by}</td>
                  <td className="p-3 text-right font-mono-accent font-semibold">${Number(v.amount).toFixed(2)}</td>
                  {canRestore && <td className="p-2"><Button size="sm" variant="outline" disabled={busy === v.id} onClick={() => restore(v)} className="gap-1" data-testid={`voided-restore-${v.id}`}><RotateCcw size={13} /> Restore</Button></td>}
                </tr>
              ))}
              {pg.filtered.length === 0 && <tr><td className="p-3 text-muted-foreground" colSpan={7}>No deleted payments.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
